<template>
  <transition name="modal">
    <div
      class="modal-mask account-popup-mask"
      @click.self="$router.replace('/')"
    >
      <PopupPanel dark class="modal-container">
        <PopupHeader class="modal-header">
          <slot name="header">
            <h3>Verify your email</h3>
            <PopupClose @click="$router.replace('/')" />
          </slot>
        </PopupHeader>

        <div class="modal-body">
          <slot name="body">
            <div class="content">
              <div class="logo">
                <img src="../assets/logo-n.svg" alt="Nvoko Logo" />
              </div>
              <h4>Enter the 6-digit code we sent to</h4>
              <span class="email-pill">{{ user.email }}</span>
              <div class="code-container">
                <div
                  v-for="i in 6"
                  :key="i"
                  contenteditable
                  class="code-digit"
                  :class="{ filled: code[i - 1] }"
                  :tabindex="i - 1 === activeIndex() ? 0 : -1"
                  inputmode="numeric"
                  :aria-label="`Digit ${i} of 6`"
                  :id="`code${i - 1}`"
                  @input="onCodeInput"
                  @keydown="onCodeKeydown"
                  @focus="focusActiveDigit()"
                  @paste.prevent="onCodePaste"
                ></div>
              </div>
              <button
                @click="submit()"
                class="primary"
                :disabled="!enableSubmitButton || submitting"
              >
                {{ submitting ? "Loading..." : "Continue" }}
              </button>
              <span class="resend" v-if="resending">Resending...</span>
              <span
                v-else
                class="resend"
                @click="countDown ? 0 : resend()"
                :class="{ 'resend-active': !countDown }"
              >
                Didn't receive the code?
                <b>{{
                  countDown ? `Resend in ${countDown}s` : "Resend code"
                }}</b>
              </span>
            </div>
          </slot>
        </div>
      </PopupPanel>
    </div>
  </transition>
</template>

<script>
import RESEND_VERIFICATION_CODE from "@/api/mutations/RESEND_VERIFICATION_CODE.gql";
import COMPLETE_VERIFICATION from "@/api/mutations/COMPLETE_VERIFICATION.gql";

export default {
  name: "SignupVerification",

  data() {
    return {
      valid: false,
      code: [],
      countDown: 59,
      resending: false,
      submitting: false
    };
  },
  computed: {
    user() {
      return this.$store.getters["account/getUser"];
    },
    enableSubmitButton() {
      return this.code.join("").length === 6;
    }
  },
  mounted() {
    document.querySelector("#code0").focus();
    this.countDownTimer();
  },
  methods: {
    // The code is entered strictly in order: digits are only appended to the
    // end or removed from the end, and focus always sits on the next empty box.
    digitEl(i) {
      return document.querySelector(`#code${i}`);
    },
    activeIndex() {
      return Math.min(this.code.length, 5);
    },
    setCode(digits) {
      this.code = digits.slice(0, 6).split("");
      for (let i = 0; i < 6; i++)
        this.digitEl(i).innerText = this.code[i] || "";
      this.focusActiveDigit();
    },
    focusActiveDigit() {
      const input = this.digitEl(this.activeIndex());
      if (document.activeElement !== input) input.focus();
      // Keep the caret after the digit so native edits behave predictably
      const range = document.createRange();
      range.selectNodeContents(input);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },
    onCodeKeydown(e) {
      const current = this.code.join("");
      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        this.setCode(current.slice(0, -1));
      } else if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        if (current.length < 6) this.setCode(current + e.key);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (this.enableSubmitButton) this.submit();
      } else if (
        (e.key.length === 1 || e.key.startsWith("Arrow")) &&
        !e.metaKey &&
        !e.ctrlKey
      ) {
        e.preventDefault();
      }
    },
    // Fallback for virtual keyboards that don't report keys on keydown
    onCodeInput(e) {
      const current = this.code.join("");
      const i = this.activeIndex();
      const typed = e.target.innerText.replace(/[^0-9]/g, "");
      const before = this.code[i] || "";
      if (typed.length < before.length) this.setCode(current.slice(0, -1));
      else if (current.length < 6 && typed.length > before.length)
        this.setCode(current + typed.replace(before, ""));
      else this.setCode(current);
    },
    onCodePaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData("text");
      const digits = text.replace(/[^0-9]/g, "");
      if (!digits) return;
      this.setCode(digits.length >= 6 ? digits : this.code.join("") + digits);
    },
    countDownTimer() {
      if (this.countDown > 0) {
        setTimeout(() => {
          this.countDown -= 1;
          this.countDownTimer();
        }, 1000);
      }
    },
    async submit() {
      if (this.submitting) return;
      try {
        this.submitting = true;
        const result = await this.$apollo.mutate({
          mutation: COMPLETE_VERIFICATION,
          variables: { code: this.code.join("") }
        });
        const { complete_verification: update } = result.data;
        this.$token = update.token;
        this.$store.commit("account/authSuccess", update);
        this.$router.push({ name: "Onboarding" });
      } catch (e) {
        this.$errorService.handleApolloError(e, "verification");
      } finally {
        this.submitting = false;
      }
    },
    async resend() {
      if (this.resending) return;
      try {
        this.resending = true;
        const result = await this.$apollo.mutate({
          mutation: RESEND_VERIFICATION_CODE
        });
        const { resend_verification_code } = result.data;
        if (resend_verification_code.success) this.countDown = 59;
      } catch (e) {
        this.$errorService.handleApolloError(e, "resend verification");
      } finally {
        this.resending = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_mixins.scss";

.account-popup-mask {
  @include auth-backdrop;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-container {
  --popup-max-width: 520px;
  --popup-surface: transparent;
  --popup-header-surface: transparent;
  --popup-line: rgba(255, 255, 255, 0.08);
  --popup-header-line: #{rgba($accent, 0.45)};
  position: relative;
  width: 100%;
  height: auto;
  margin: 0;
  @include auth-glass-card;
}

.modal-header {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.modal-body {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 36px 32px 32px !important;

  .content {
    display: flex;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    width: 100%;
    text-align: center;

    .logo {
      position: relative;
      display: grid;
      place-items: center;
      width: 88px;
      height: 88px;
      margin-bottom: 24px;
      border-radius: 24px;
      background: linear-gradient(
        145deg,
        rgba(255, 255, 255, 0.07),
        rgba(255, 255, 255, 0.02)
      );
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 40px rgba($accent, 0.25);

      img {
        width: 52px;
        height: auto;
      }
    }

    h4 {
      color: rgba(255, 255, 255, 0.78);
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      margin-bottom: 10px;
    }

    .email-pill {
      max-width: 100%;
      margin-bottom: 32px;
      padding: 6px 14px;
      border-radius: 999px;
      background: rgba($accent, 0.12);
      border: 1px solid rgba($accent, 0.3);
      color: #ffd3e3;
      font-size: 14px;
      font-weight: 500;
      overflow-wrap: anywhere;
    }

    .code-container {
      width: 100%;
      max-width: 400px;
      display: flex;
      justify-content: center;
      gap: 10px;
      margin-bottom: 32px;

      .code-digit {
        flex: 1 1 0;
        min-width: 0;
        height: 64px;
        line-height: 62px;
        text-align: center;
        font-size: 28px;
        font-weight: 600;
        color: $white;
        caret-color: $accent;
        border: 1px solid rgba(255, 255, 255, 0.14);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.04);
        outline: none;
        transition: border-color 160ms ease, box-shadow 160ms ease,
          background-color 160ms ease;

        &:hover {
          border-color: rgba(255, 255, 255, 0.24);
        }

        &.filled {
          border-color: rgba($accent, 0.5);
          background: rgba($accent, 0.08);
        }

        &:focus {
          border-color: $accent;
          background: rgba($accent, 0.1);
          box-shadow: 0 0 0 4px rgba($accent, 0.18);
        }
      }
    }

    button {
      width: 100%;
      max-width: 400px;
      min-height: 48px;
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: -0.22px;
      padding: 12px 30px;
      border-radius: 999px;
      color: $white;
      background: linear-gradient(135deg, $accent, darken($accent, 15%));
      box-shadow: 0 8px 24px rgba($accent, 0.3);
      transition: transform 160ms ease, box-shadow 160ms ease,
        opacity 160ms ease;

      &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 12px 30px rgba($accent, 0.4);
      }

      &:disabled {
        opacity: 0.45;
        box-shadow: none;
        cursor: not-allowed;
      }
    }

    .resend {
      color: rgba(255, 255, 255, 0.6);
      font-size: 14px;
      margin-top: 20px;

      b {
        font-weight: 500;
        color: rgba(255, 255, 255, 0.75);
      }
    }

    .resend-active {
      cursor: pointer;

      b {
        color: $accent;
      }

      &:hover b {
        text-decoration: underline;
      }
    }
  }
}

@media (max-width: 480px) {
  .modal-body {
    padding: 28px 20px 24px !important;

    .content .code-container {
      gap: 6px;

      .code-digit {
        height: 54px;
        line-height: 52px;
        font-size: 24px;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .modal-body .content :is(button, .code-digit) {
    transition: none;
  }
}
</style>
